class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = this.querySelector('#CartDrawer');
    this.overlay = this.querySelector('#CartDrawer-Overlay');
    this.inner = this.querySelector('.drawer__inner');

    this._cbhScrollClass = 'overflow-hidden';
    this._cbhOpenClass = 'cbh-cart-drawer-open';
    this._cbhHydrationPromise = null;

    this.onKeyUp = this.onKeyUp.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onClick = this.onClick.bind(this);

    this.addEventListener('keyup', this.onKeyUp);
    this.addEventListener('click', this.onClick);

    // syncDrawerState() is the single authority for binding the replaceable overlay.
    // Binding here as well would register the initial overlay twice.
    this.setHeaderCartIconAccessibility();
    this.syncDrawerState();
  }

  onKeyUp(evt) {
    const key = String(evt.key || evt.code || '').toUpperCase();
    if (key === 'ESCAPE') this.close();
  }

  onOverlayClick() {
    this.close();
  }

  onClick(evt) {
    const closeBtn = evt.target && evt.target.closest ? evt.target.closest('.drawer__close') : null;
    if (!closeBtn) return;

    evt.preventDefault();
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', this.classList.contains('active') ? 'true' : 'false');

    const warmDrawer = () => {
      this.hydrate().catch(() => {});
    };

    cartLink.addEventListener('pointerenter', warmDrawer, { passive: true });
    cartLink.addEventListener('focusin', warmDrawer);
    cartLink.addEventListener('touchstart', warmDrawer, { passive: true });

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      const code = event.code ? event.code.toUpperCase() : '';
      if (code === 'SPACE' || code === 'ENTER') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const ready = await this.hydrate();
    if (!ready) {
      const cartUrl =
        (window.routes && window.routes.cart_url) ||
        (typeof routes !== 'undefined' && routes && routes.cart_url) ||
        '/cart';
      window.location.assign(cartUrl);
      return;
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.updateViewportHeight();

    window.setTimeout(() => {
      this.classList.add('animate', 'active');
      this.setOpenState(true);
    }, 0);

    this.lockScroll();
    this.trapFocusSoon();
  }

  hasHydratedContent() {
    if (!this.hasAttribute('data-cbh-lazy-cart-drawer')) return true;

    const liveInner = this.querySelector('.drawer__inner');
    return !!(liveInner && liveInner.children.length > 0);
  }

  markHydrated(parsedState = null) {
    this.removeAttribute('data-cbh-lazy-cart-drawer');
    const liveInner = this.querySelector('.drawer__inner');
    if (liveInner) liveInner.removeAttribute('aria-busy');

    if (parsedState) {
      this.syncDrawerState(parsedState);
      return;
    }

    this.drawer = this.querySelector('#CartDrawer');
    this.overlay = this.querySelector('#CartDrawer-Overlay');
    this.inner = this.querySelector('.drawer__inner');
    if (this.overlay && !this.overlay.dataset.cbhBound) {
      this.overlay.addEventListener('click', this.onOverlayClick);
      this.overlay.dataset.cbhBound = 'true';
    }
  }

  hydrateFromSectionHTML(html) {
    if (!html || !this.drawer) return false;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sourceDrawerElement = doc.querySelector('cart-drawer');
    const sourceDrawer = sourceDrawerElement?.querySelector('#CartDrawer');
    if (!sourceDrawer) return false;

    this.drawer.innerHTML = sourceDrawer.innerHTML;

    const count = Number(sourceDrawerElement?.dataset?.cartItemCount);
    if (Number.isFinite(count)) this.setAttribute('data-cart-item-count', String(count));
    this.classList.toggle('is-empty', sourceDrawerElement?.classList.contains('is-empty'));

    this.markHydrated();
    return true;
  }

  hydrate() {
    if (this.hasHydratedContent()) {
      if (this.hasAttribute('data-cbh-lazy-cart-drawer')) this.markHydrated();
      return Promise.resolve(true);
    }

    if (this._cbhHydrationPromise) return this._cbhHydrationPromise;

    this._cbhHydrationPromise = (async () => {
      try {
        const cartUrl =
          (window.routes && window.routes.cart_url) ||
          (typeof routes !== 'undefined' && routes && routes.cart_url) ||
          '/cart';
        const url = new URL(cartUrl, window.location.href);
        url.searchParams.set('section_id', 'cart-drawer');

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) return false;

        const html = await response.text();

        // A cart mutation may have populated the shell while the intent fetch
        // was in flight. Never overwrite that newer, server-authoritative state.
        if (this.hasHydratedContent()) {
          if (this.hasAttribute('data-cbh-lazy-cart-drawer')) this.markHydrated();
          return true;
        }

        return this.hydrateFromSectionHTML(html);
      } catch (error) {
        window.cbhLogError?.(error);
        return false;
      } finally {
        this._cbhHydrationPromise = null;
      }
    })();

    return this._cbhHydrationPromise;
  }

  close() {
    this.classList.remove('active');
    this.setOpenState(false);

    try {
      removeTrapFocus(this.activeElement);
    } catch (e) {}

    this.unlockScroll();

    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      window.setTimeout(() => this.activeElement.focus({ preventScroll: true }), 0);
    }
  }

  setOpenState(isOpen) {
    document.documentElement.classList.toggle(this._cbhOpenClass, isOpen);
    document.body.classList.toggle(this._cbhOpenClass, isOpen);

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  updateViewportHeight() {
    try {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    } catch (e) {}
  }

  trapFocusSoon() {
    const doTrap = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : (this.drawer || this);

      const focusElement =
        this.querySelector('.drawer__close') ||
        this.querySelector('.drawer__inner') ||
        containerToTrapFocusOn;

      try {
        trapFocus(containerToTrapFocusOn, focusElement);
      } catch (e) {}
    };

    requestAnimationFrame(() => {
      doTrap();
      requestAnimationFrame(doTrap);
    });
  }

  lockScroll() {
    if (window.CBH_SCROLL_LOCK && typeof window.CBH_SCROLL_LOCK.lock === 'function') {
      window.CBH_SCROLL_LOCK.lock(this._cbhScrollClass);
      return;
    }

    document.body.classList.add(this._cbhScrollClass);
  }

  unlockScroll() {
    if (window.CBH_SCROLL_LOCK && typeof window.CBH_SCROLL_LOCK.unlock === 'function') {
      window.CBH_SCROLL_LOCK.unlock(this._cbhScrollClass);
      return;
    }

    document.body.classList.remove(this._cbhScrollClass);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    // /cart/add responses don't always expose a full-cart item_count. The
    // rendered drawer section does, so use it as the server-authoritative
    // fallback before syncing the outer custom element state.
    if (parsedState && typeof parsedState.item_count === 'undefined' && parsedState.sections?.['cart-drawer']) {
      try {
        const drawerDoc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
        const renderedDrawer = drawerDoc.querySelector('cart-drawer');
        const renderedCount = Number(renderedDrawer?.dataset?.cartItemCount);
        if (Number.isFinite(renderedCount)) parsedState.item_count = renderedCount;
      } catch (e) {}
    }

    this.getSectionsToRender().forEach((section) => {
      const target = document.querySelector(section.target || section.selector || `#${section.id}`);
      if (!target) return;

      const nextHTML = parsedState.sections && parsedState.sections[section.id];
      if (!nextHTML) return;

      const sourceSelector = section.source || section.selector || '.shopify-section';
      target.innerHTML = this.getSectionInnerHTML(nextHTML, sourceSelector);
    });

    if (this.querySelector('.drawer__inner')?.children.length) this.markHydrated(parsedState);
    else this.syncDrawerState(parsedState);

    if (this.classList.contains('active')) {
      this.setOpenState(true);
      this.trapFocusSoon();
    }
  }

  syncDrawerState(parsedState) {
    const itemCount = Number(parsedState && typeof parsedState.item_count !== 'undefined'
      ? parsedState.item_count
      : this.getAttribute('data-cart-item-count'));

    if (!Number.isNaN(itemCount)) {
      this.setAttribute('data-cart-item-count', String(itemCount));
      this.classList.toggle('is-empty', itemCount === 0);
    }

    this.drawer = this.querySelector('#CartDrawer');
    this.overlay = this.querySelector('#CartDrawer-Overlay');
    this.inner = this.querySelector('.drawer__inner');

    if (this.overlay && !this.overlay.dataset.cbhBound) {
      this.overlay.addEventListener('click', this.onOverlayClick);
      this.overlay.dataset.cbhBound = 'true';
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        target: '#CartDrawer',
        source: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        target: '#cart-icon-bubble',
        source: '.shopify-section',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      ...super.getSectionsToRender(),
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
